import { useTranslation } from "react-i18next";

import type { PracticeQuestionForClient } from "@/lib/practice-api";

/**
 * apps/web :: components/practice :: QuestionCard
 *
 * Presentational wrapper for the question prompt. Displays the question
 * text, the category tag (lowercased for readability — the wire enum is
 * UPPER_SNAKE), and a difficulty indicator (1-5). No interactive state
 * lives here; the surrounding PracticeSession owns selection + submit.
 *
 * i18n keys are purpose-keyed per the frontend-design SKILL — we translate
 * "practice.category.<wire_name_lower>" with a defaultValue fallback so
 * un-localised categories still render the raw underscore-free label.
 */
export interface QuestionCardProps {
  question: PracticeQuestionForClient;
}

export default function QuestionCard({ question }: QuestionCardProps) {
  const { t } = useTranslation();
  const categoryKey = question.category.toLowerCase();
  const categoryLabel = t(`practice.category.${categoryKey}`, {
    defaultValue: categoryKey.replaceAll("_", " "),
  });

  return (
    <section
      data-testid="practice-question-card"
      aria-labelledby="practice-question-heading"
      className="rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
    >
      <div className="flex items-center justify-between gap-sm text-xs">
        <span
          data-testid="practice-question-category"
          className="rounded-full bg-brand-primary/10 px-sm py-xs font-semibold uppercase tracking-wide text-brand-primary"
        >
          {categoryLabel}
        </span>
        <span
          data-testid="practice-question-difficulty"
          aria-label={t("practice.difficulty.label", {
            n: question.difficulty,
          })}
          className="text-neutral-600"
        >
          {t("practice.difficulty.value", {
            n: question.difficulty,
            defaultValue: `Level ${question.difficulty}`,
          })}
        </span>
      </div>
      <h2
        id="practice-question-heading"
        data-testid="practice-question-text"
        className="mt-md font-display text-xl font-bold leading-snug text-brand-primary sm:text-2xl"
      >
        {question.question_text}
      </h2>
    </section>
  );
}
